import { eventChannel, END } from 'redux-saga';
import {
    all,
    fork,
    put,
    takeLatest,
    call,
    take,
    cancel,
    cancelled,
    delay,
    select,
} from 'redux-saga/effects';

import { getJobs as getJobsApi } from '../../helpers';
import { buildJobsUrl } from '../../helpers/api/jobs';
import { jobResponseToJobObject } from '../../helpers/mappers/job';
import {
    jobsApiResponseError,
    jobsApiResponseSuccess,
    jobsStreamConnected,
    jobsStreamDisconnected,
    jobsStreamError,
    upsertJobFromStream,
    deleteJobFromStream,
} from './actions';
import { JobsActionTypes } from './constants';

export function* fetchJobs({ payload: { query } = {} }) {
    try {
        const response = yield call(getJobsApi, query);
        const jobs = (response?.data || []).map((job) => jobResponseToJobObject(job)).filter(Boolean);
        yield put(jobsApiResponseSuccess(JobsActionTypes.GET_JOBS, jobs));
    } catch (error) {
        yield put(jobsApiResponseError(JobsActionTypes.GET_JOBS, error));
    }
}

const parseSseEvent = (rawEvent) => {
    if (!rawEvent || rawEvent.trim().length === 0 || rawEvent.startsWith(':')) {
        return null;
    }
    const lines = rawEvent.split('\n');
    let eventName = 'message';
    const dataLines = [];

    lines.forEach((line) => {
        const trimmed = line.trim();
        if (trimmed.startsWith('event:')) {
            eventName = trimmed.replace('event:', '').trim();
        }
        if (trimmed.startsWith('data:')) {
            dataLines.push(trimmed.replace('data:', '').trim());
        }
    });

    if (dataLines.length === 0) {
        return null;
    }

    const dataString = dataLines.join('\n');
    let parsedData;
    try {
        parsedData = JSON.parse(dataString);
    } catch (err) {
        parsedData = dataString;
    }
    return { event: eventName, data: parsedData };
};

const createJobsEventChannel = (token, baseUrl) =>
    eventChannel((emit) => {
        if (!baseUrl) {
            emit({ type: 'error', error: new Error('Jobs URL is not configured') });
            emit(END);
            return () => {};
        }

        const controller = new AbortController();
        const decoder = new TextDecoder();
        const streamUrl = `${baseUrl.replace(/\/$/, '')}/stream`;
        let buffer = '';

        fetch(streamUrl, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
        })
            .then((response) => {
                if (!response?.ok) {
                    emit({ type: 'error', error: new Error(`Stream error: ${response?.status}`) });
                    emit(END);
                    return;
                }
                if (!response.body) {
                    emit({ type: 'error', error: new Error('Jobs stream missing body') });
                    emit(END);
                    return;
                }

                const reader = response.body.getReader();

                const read = () =>
                    reader
                        .read()
                        .then(({ done, value }) => {
                            if (done) {
                                emit(END);
                                return;
                            }

                            if (value) {
                                buffer += decoder.decode(value, { stream: true });
                                buffer = buffer.replace(/\r\n/g, '\n');

                                let boundary = buffer.indexOf('\n\n');
                                while (boundary !== -1) {
                                    const rawEvent = buffer.slice(0, boundary);
                                    buffer = buffer.slice(boundary + 2);
                                    const parsed = parseSseEvent(rawEvent);
                                    if (parsed) {
                                        emit({ type: 'event', ...parsed });
                                    }
                                    boundary = buffer.indexOf('\n\n');
                                }
                            }
                            read();
                        })
                        .catch((error) => {
                            emit({ type: 'error', error });
                            emit(END);
                        });

                read();
            })
            .catch((error) => {
                emit({ type: 'error', error });
                emit(END);
            });

        return () => controller.abort();
    });

function* handleJobsStream() {
    while (true) {
        const token = yield select((state) => state.Auth?.user?.token);
        const baseUrl = buildJobsUrl();
        if (!token) {
            return;
        }
        if (!baseUrl) {
            yield put(jobsStreamError('Jobs URL is not configured'));
            return;
        }

        const channel = yield call(createJobsEventChannel, token, baseUrl);
        yield put(jobsStreamConnected());

        try {
            while (true) {
                const payload = yield take(channel);

                if (!payload || payload === END) {
                    break;
                }

                if (payload.type === 'error') {
                    yield put(jobsStreamError(payload.error?.message || 'Jobs stream error'));
                    break;
                }

                if (payload.event === 'job-deleted') {
                    yield put(deleteJobFromStream(payload.data));
                } else if (payload.data) {
                    const job = jobResponseToJobObject(payload.data);
                    if (job) {
                        yield put(upsertJobFromStream(job));
                    }
                }
            }
        } finally {
            const wasCancelled = yield cancelled();
            channel.close();
            yield put(jobsStreamDisconnected());
            if (wasCancelled) {
                break;
            }
        }

        // retry after a short pause
        yield delay(5000);
    }
}

function* watchGetJobs() {
    yield takeLatest(JobsActionTypes.GET_JOBS, fetchJobs);
}

function* watchJobsStreamLifecycle() {
    let streamTask;
    while (true) {
        const action = yield take([JobsActionTypes.START_JOBS_STREAM, JobsActionTypes.STOP_JOBS_STREAM]);
        if (action.type === JobsActionTypes.START_JOBS_STREAM) {
            if (streamTask) {
                yield cancel(streamTask);
            }
            streamTask = yield fork(handleJobsStream);
        } else if (action.type === JobsActionTypes.STOP_JOBS_STREAM && streamTask) {
            yield cancel(streamTask);
            streamTask = null;
        }
    }
}

function* jobsSaga() {
    yield all([fork(watchGetJobs), fork(watchJobsStreamLifecycle)]);
}

export default jobsSaga;
